import { useEffect } from 'react';
import useQuery from '@/hooks/useQuery';
import { getUnixTimestamp } from '@/helpers/dateTimeHelper';
import { zenStartDate } from '@/components/Zen/ZenHelper';
import { GrowthBook, configureCache } from '@growthbook/growthbook';
import { autoAttributesPlugin } from '@growthbook/growthbook/plugins';
import { Api } from '@/api';
import {
  EXPERIMENTS_API_URL,
  EXPERIMENTS_CLIENT_KEY,
  EXPERIMENTS_DEV_MODE
} from '@/config/settings';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import Rand from 'rand-seed';

const ZennerDisabled = Object.freeze({
  zenModeEnabled: false,
  templateSlugOverride: false
});

/**
 * A small wrapper for enabling the use of zenner
 * @param {?Date|undefined} quizDateCreated - Optional creation date for the quiz, used to check if we should use zenner mode or not
 * @param {?String|undefined} currentTemplateSlug - Optional template slug, used to know if we should do an early return if a quiz is already zenner mode
 * @return {{ zenModeEnabled: boolean, templateSlugOverride: boolean, }}
 */
export const useZennerMode = ({
  quizDateCreated,
  currentTemplateSlug,
  forceTemplateSlug
}) => {
  const forceFlowType = useQuery().get('forceFlowType');

  if (forceFlowType) {
    if (forceFlowType === 'zen') {
      return {
        zenModeEnabled: true,
        templateSlugOverride: true
      };
    } else if (forceFlowType === 'normal') {
      return ZennerDisabled;
    }

    console.warn(
      'Invalid forceFlowType given: ' +
        forceFlowType +
        ' (Only "zen" and "normal" are supported)'
    );
  }

  if (quizDateCreated && getUnixTimestamp(quizDateCreated) < zenStartDate) {
    return ZennerDisabled;
  }

  // If the template slug includes 'ZennerMode' then it's enabled by default
  if (
    currentTemplateSlug?.length > 0 &&
    currentTemplateSlug.includes('ZennerMode')
  ) {
    return {
      zenModeEnabled: true,
      templateSlugOverride: true
    };
  } else if (forceTemplateSlug && currentTemplateSlug?.length > 0) {
    // If the template slug is forced and it's not zenner mode, then it's disabled by default
    return {
      zenModeEnabled: false,
      templateSlugOverride: false
    };
  }

  // If the template slug is not forced and it's not zenner mode, then it's enabled by default
  return {
    zenModeEnabled: true,
    templateSlugOverride: true
  };
};

configureCache({
  cacheKey: 'gbEediFeatureCache',
  staleTTL: 1000 * 30,
  maxAge: 1000 * 60 * 60 * 24,
  // Note: Cache is disable due to some user's somehow still being
  // served experiments that have since been disabled.
  // Even after attempting to invalidate the cache and force refetching features
  // some users still have stale cache.
  //
  // For now, keep it off, the load is fairly small.
  // If performance ever becomes an issue, we can spin up some growthbook proxies
  // https://docs.growthbook.io/self-host/proxy
  disableCache: true
});

export const growthbook = new GrowthBook({
  apiHost: EXPERIMENTS_API_URL,
  clientKey: EXPERIMENTS_CLIENT_KEY,
  enableDevMode: EXPERIMENTS_DEV_MODE,
  trackingCallback: (experiment, result) => {
    const attributes = growthbook.getAttributes();
    const userId = attributes.userId;
    if (!userId) {
      console.warn('No userId in attributes, assignment not tracked.');
      return;
    }

    Api.trackExperimentAssignment(
      userId,
      experiment.hashAttribute,
      result.hashValue,
      experiment.key,
      result.variationId,
      getVariationName(result, experiment)
    ).catch(error => {
      console.error('Failed to track assignment:', error);
    });
  },
  plugins: [autoAttributesPlugin()],
  backgroundSync: true,
  subscribeToChanges: true
});

// Conditionally call the useFeature hook by placing this component behind a boolean to control the triggered tracking callback
export const ConditionalFeatureIsOnHandler = ({ flagName, onLoad }) => {
  const isEnabled = useFeatureIsOn(flagName);

  useEffect(() => {
    onLoad(isEnabled);
  }, [isEnabled, onLoad]);

  return null;
};

export function getVariationName(result, experiment) {
  if (!result || !experiment?.meta) {
    return null;
  }
  const variationMeta = experiment.meta.find(
    metaItem => metaItem.key == result.variationId
  );

  return variationMeta ? variationMeta.name : null;
}

export const MisdisExperiments = Object.freeze({
  None: 0,
  CR: 1,
  ER: 2
});

/**
 * Returns the current experiment the user is involved with (can only ever be one)
 * @returns {{experiment: number, group: ?string}} - Experiment is tied to MisdisExperiments 'enum'. Group is the experiment data
 */
export function useGetMisdisExeriment() {
  // const cr = useFeature('MisDis-CR');
  // const er = useFeature('MisDis-ER');
  //
  // if (cr && cr.on && !cr.off) {
  //   return {
  //     experiment: MisdisExperiments.CR,
  //     group: cr.value
  //   };
  // }
  //
  // if (er && er.on && !er.off) {
  //   return {
  //     experiment: MisdisExperiments.ER,
  //     group: er.value
  //   };
  // }

  return {
    experiment: MisdisExperiments.None,
    group: null
  };
}

/**
 * A generic func to know if the current experiment should be shown
 * @param {number} value - Should be whole number between 0 and 100, treated as a %
 * @param {number|string} seed - Some value to be used as a rng seed
 * @return {boolean} - Should the experiment be shown?
 */
export function shouldMisDisShow(value, seed) {
  console.assert(
    typeof value === 'number',
    'shouldMisDisShow value must be a number',
    value
  );
  console.assert(
    value >= 0 && value <= 100,
    'shouldMisDisShow value not between 0-100',
    value
  );

  if (value >= 100) return true;
  if (value <= 0) return false;

  const rand = new Rand(String(seed));

  return Math.floor(rand.next() * 100) < value;
}

const EmojiList = ['🧠', '🚀', '👀', '🎈', '💥', '🫧', '🤞', '💪'];

export const FloatingEmoji = event => {
  if (event?.pageX === undefined || event?.pageY === undefined) return;

  const reaction = document.createElement('div');
  reaction.append(
    document.createTextNode(
      EmojiList[Math.floor(Math.random() * EmojiList.length)]
    )
  );
  reaction.classList.add('reaction');

  document.body.appendChild(reaction);

  const lifeTime = 0.5 + Math.random();

  reaction.style.cssText = `left: ${event.pageX - 10}px; top: ${event.pageY - 10}px; scale: ${1 + Math.random()}; animation: reaction-wiggle ${0.5 + Math.random() * 2}s ease-in-out infinite, fade-out ${lifeTime}s linear;`;

  setTimeout(() => {
    reaction.remove();
  }, lifeTime * 1000);

  return reaction;
};
